'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMenu } from '@/providers/menu-provider';
import { useEntityContext } from '@/providers/entity-context-provider';
import { cn } from '@/lib/utils';
import { X, Factory, Home } from 'lucide-react';
import {
  AccordionMenu,
  AccordionMenuGroup,
  AccordionMenuItem,
  AccordionMenuLabel,
  AccordionMenuSub,
  AccordionMenuSubContent,
  AccordionMenuSubTrigger,
} from '@/components/ui/accordion-menu-fixed';
import { Badge } from '@/components/ui/badge';

// Smart Link Component - unterscheidet zwischen Next.js und externen Links
function SmartLink({ href, isExternal, target, children, className, router, ...props }) {
  
  // Setze Navigation-Daten als data-Attribut für AccordionMenu-Handler
  const divProps = {
    className: `${className} flex items-center grow gap-2 w-full cursor-pointer`,
    'data-navigation': JSON.stringify({ href, isExternal, target }),
    ...props
  };

  return (
    <div {...divProps}>
      {children}
    </div>
  );
}

export function SidebarMenu() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchKey = searchParams?.toString() || '';
  const router = useRouter();
  const { menuData, isLoading } = useMenu();
  const { selectedEntity, contextMenu, clearEntity, isLoading: contextLoading } = useEntityContext();

  // Match a menu item's path against the current URL — pathname AND query.
  // Pathname: exact match or prefix-segment match.
  // Query:
  //   - menu has query (e.g. "?a=offen") → ALL menu keys must match current query
  //   - menu has NO query → match only when current URL has no non-auth params
  //     (auth params token/theme/lang are managed by parent shell, ignored here).
  // This lets sibling menu items like Übersicht (/messungen) / offen
  // (/messungen?a=offen) / erledigt (/messungen?a=ok) highlight independently.
  const matchPath = useCallback(
    (path) => {
      if (!path) return false;
      const qIdx = path.indexOf('?');
      const pathPath = qIdx >= 0 ? path.slice(0, qIdx) : path;
      const pathQuery = qIdx >= 0 ? path.slice(qIdx + 1) : '';

      // Module index `/app/{slug}` is prefix of every sub-route — exact only.
      const segs = pathPath.split('/').filter(Boolean);
      const isModuleIndex = segs.length === 2 && segs[0] === 'app';
      const pathnameMatch =
        pathPath === pathname ||
        (pathPath.length > 1 && !isModuleIndex && pathname.startsWith(pathPath + '/'));
      if (!pathnameMatch) return false;

      const curParams = new URLSearchParams(searchKey);
      ['token', 'theme', 'lang'].forEach((k) => curParams.delete(k));

      if (pathQuery) {
        const menuParams = new URLSearchParams(pathQuery);
        for (const [k, v] of menuParams.entries()) {
          if (curParams.get(k) !== v) return false;
        }
        return true;
      }
      return curParams.toString() === '';
    },
    [pathname, searchKey],
  );

  // Auto-expand: find top-level item that contains the active path
  const [openValue, setOpenValue] = useState(null);

  // Liegt die aktuelle Seite auf diesem Menuepunkt oder darunter? (auch fuer Punkte,
  // die selbst Unterpunkte haben, z.B. KHG "Offert Vergleich" -> /khg/offert_vergleich/{c}/{a}/{art})
  const isOnActiveBranch = useCallback((it) => {
    if (it?.path && it.path !== '#' && matchPath(it.path)) return true;
    return !!it?.children?.some((c) => isOnActiveBranch(c));
  }, [matchPath]);

  // Wert des Unterpunkts, der in einer Unter-Ebene offen sein soll (sonst undefined).
  // Muss dieselbe Wert-Bildung nutzen wie buildMenuItemChild*/buildMenuItemChildWithBadge.
  const activeChildValue = (children, level) => {
    const idx = (children || []).findIndex((c) => c?.children?.length > 0 && isOnActiveBranch(c));
    if (idx < 0) return undefined;
    const c = children[idx];
    return c.id ? `menu-${c.id}` : (c.path || `child-${level}-${idx}`);
  };

  const findActiveParentValue = useCallback((items, currentPath) => {
    for (const item of items) {
      if (!item.children || item.children.length === 0) continue;
      const itemValue = item.id ? `menu-${item.id}` : (item.path || '');
      const hasActiveChild = (children) => {
        for (const child of children) {
          if (child.path && (currentPath === child.path || currentPath.startsWith(child.path + '/'))) return true;
          if (child.children && hasActiveChild(child.children)) return true;
        }
        return false;
      };
      if (hasActiveChild(item.children)) return itemValue;
    }
    return null;
  }, []);

  useEffect(() => {
    if (!menuData || !menuData.length) return;
    const value = findActiveParentValue(menuData, pathname);
    if (value) setOpenValue(value);
  }, [pathname, menuData, findActiveParentValue]);

  // Handle navigation for accordion items
  const handleItemClick = useCallback((value, e) => {
    // Parse navigation data from the clicked element
    const navData = e.target.closest('[data-navigation]')?.dataset.navigation;
    if (navData) {
      try {
        const { href, isExternal, target } = JSON.parse(navData);
        
        
        // Externe Links (Laravel/XSite) - AJAX Loading via ExternalContentLoader
        if (isExternal && href && href !== '#') {
          
          
          // Extrahiere Menu-Titel für Browser-Titel
          const clickedElement = e.target.closest('[data-navigation]');
          const menuTitle = clickedElement?.textContent?.trim() || 'Laravel Content';
          
          // Dispatche CustomEvent für ExternalContentLoader
          const customEvent = new CustomEvent('loadExternalContent', {
            detail: { 
              url: href, 
              title: menuTitle
            }
          });
          window.dispatchEvent(customEvent);
          
          // Browser-URL sofort ändern (ohne Reload!)
          window.history.pushState(
            { xsitePath: href, pageTitle: menuTitle },
            menuTitle,
            href
          );
          
          // Browser-Titel setzen
          document.title = `${menuTitle} | XOFFI`;
          
          
          return;
        }
        
        // _blank Links
        if (target === '_blank' && href && href !== '#') {
          
          window.open(href, '_blank');
          return;
        }
        
        // Interne Next.js Links
        if (href && href !== '#') {
          
          router.push(href);
          return;
        }
      } catch (error) {
        console.error('[SidebarMenu] Error parsing navigation data:', error);
      }
    }
  }, [router]);

  // Verwende nur dynamisches Menü (kein Fallback)
  const currentMenu = menuData || [];

  // Global classNames for consistent styling
  const classNames = {
    // space-y-0 → no gap between top-level items / module groups
    root: 'lg:ps-1 space-y-0',
    group: 'gap-0',
    label:
      'uppercase text-xs font-medium text-muted-foreground/70 pt-2.25 pb-px',
    separator: '',
    item: 'h-7 hover:bg-transparent text-accent-foreground hover:text-primary data-[selected=true]:text-primary data-[selected=true]:bg-muted data-[selected=true]:font-medium',
    sub: '',
    subTrigger:
      'h-7 hover:bg-transparent text-accent-foreground hover:text-primary data-[selected=true]:text-primary data-[selected=true]:bg-muted data-[selected=true]:font-medium',
    subContent: 'py-0',
    indicator: '',
  };

  // Per-Modul Hintergrundfarbe für die Sidebar-Items (dezente Akzent-Farbe).
  // Macht visuell sofort klar zu welchem Container ein Item gehört.
  // Light/Dark-Mode-aware via Tailwind dark:-Varianten.
  const getModuleBgClass = (module) => {
    if (!module) return '';
    if (module === 'kaie')          return 'bg-gray-100 dark:bg-gray-800/40';
    if (module === 'kaie-ie')       return 'bg-emerald-50 dark:bg-emerald-950/30';
    if (module === 'kaie-hie')      return 'bg-sky-50 dark:bg-sky-950/30';
    if (module === 'kaie-gastro')   return 'bg-orange-50 dark:bg-orange-950/30';
    if (module === 'kaie-direkteinleiter') return 'bg-cyan-50 dark:bg-cyan-950/30';
    if (module === 'kaie-admin')    return 'bg-purple-50 dark:bg-purple-950/30';
    if (module === 'kaie-archiv')   return 'bg-yellow-50 dark:bg-yellow-950/30';
    if (module === 'kaie-auswertungen') return 'bg-pink-50 dark:bg-pink-950/30';
    if (module === 'kaie-berichte') return 'bg-indigo-50 dark:bg-indigo-950/30';
    return '';
  };

  // Depth-basierte Border-Farben für visuelle Hierarchie
  const getDepthBorderClass = (level) => {
    const depthBorders = [
      '',                                    // level 0: keine Border
      'border-l-2 border-l-blue-400/50',     // level 1: blau
      'border-l-2 border-l-green-400/50',    // level 2: grün
      'border-l-2 border-l-purple-400/50',   // level 3: lila
      'border-l-2 border-l-orange-400/50',   // level 4+: orange
    ];
    return depthBorders[Math.min(level, depthBorders.length - 1)] || '';
  };

  // Manifest-driven conditional visibility filter.
  // Container-Developer setzt show_when im module-manifest.json — Frontend wertet hier aus.
  // See /var/www/xoffi/.claude/rule-details/menu-manifest-spec.md
  const isVisible = (item) => {
    const sw = item.show_when;
    if (!sw || typeof sw !== 'object') return true;
    if (sw.entity_required && !selectedEntity) return false;
    if (sw.entity_type) {
      const types = Array.isArray(sw.entity_type) ? sw.entity_type : [sw.entity_type];
      if (!selectedEntity || !types.includes(selectedEntity.type)) return false;
    }
    // feature_flag / permission — to be implemented when feature-flag system lands
    return true;
  };

  // Replace {entity_name}, {entity_id}, {entity_type} placeholders in title/heading.
  // Manifest convention is e.g. "#{entity_name}#" — the wrapping '#' chars are a
  // visual marker in the old lara10 design but should NOT render in the new UI,
  // so strip leading/trailing '#' after substitution.
  const _entityReplace = (s) => {
    if (typeof s !== 'string' || !s.includes('{entity_')) return s;
    const replaced = s
      .replace(/\{entity_name\}/g, selectedEntity?.name || '')
      .replace(/\{entity_id\}/g, selectedEntity?.id || '')
      .replace(/\{entity_type\}/g, selectedEntity?.type || '');
    return replaced.replace(/^#+\s*/, '').replace(/\s*#+$/, '').trim();
  };

  // Replace {id} (manifest convention for entity-id placeholder in menu links)
  // with the currently-selected entity id. Without this, links like
  // "/app/kaie-ie/{id}/stammdaten" would stay literal and the click target
  // wouldn't match the EntityProvider's path regex → sidebar would hide
  // kaie-ie items on the very page that just got selected.
  const _pathReplace = (s) => {
    if (typeof s !== 'string' || !s.includes('{')) return s;
    return s
      .replace(/\{id\}/g, selectedEntity?.id ?? '')
      .replace(/\{entity_id\}/g, selectedEntity?.id ?? '')
      .replace(/\{entity_type\}/g, selectedEntity?.type ?? '');
  };

  const _substitutePlaceholders = (item) => {
    if (!item || typeof item !== 'object') return item;
    const next = { ...item };
    if (next.title) next.title = _entityReplace(next.title);
    if (next.heading) next.heading = _entityReplace(next.heading);
    if (next.path) next.path = _pathReplace(next.path);
    if (next.children) next.children = next.children.map(_substitutePlaceholders);
    return next;
  };

  const filterItemsByContext = (items) => {
    if (!Array.isArray(items)) return items;
    return items
      .filter(isVisible)
      .map(item => item.children ? { ...item, children: filterItemsByContext(item.children) } : item)
      .map(_substitutePlaceholders);
  };

  const buildMenu = (items) => {
    const filtered = filterItemsByContext(items);

    // Render single item (heading/disabled/badge/root)
    const renderItem = (item, index) => {
      if (item.heading) return buildMenuHeading(item, index);
      if (item.disabled) return buildMenuItemRootDisabled(item, index);
      if (item.badge_text) return buildMenuItemRootWithBadge(item, index);
      return buildMenuItemRoot(item, index);
    };

    // Group consecutive items of the SAME module into one colored block so
    // there's no `space-y-3` gap between them — items of one container stick
    // together visually, only between different modules we keep a gap.
    const out = [];
    let currentModule = null;
    let currentBatch = [];
    let groupCounter = 0;

    const flushBatch = () => {
      if (currentBatch.length === 0) return;
      const bgClass = getModuleBgClass(currentModule);
      const key = `mod-${currentModule || 'none'}-${groupCounter++}`;
      if (bgClass) {
        out.push(
          <div key={key} className={cn(bgClass, 'rounded-md -mx-1 px-1 py-0 space-y-0')}>
            {currentBatch}
          </div>
        );
      } else {
        // No background: still wrap to keep consistent spacing rules + zero gap.
        out.push(
          <div key={key} className="space-y-0">
            {currentBatch}
          </div>
        );
      }
      currentBatch = [];
    };

    filtered.forEach((item, index) => {
      if (item.divider_before) {
        flushBatch();
        out.push(<div key={`divider-${index}`} className="my-2 mx-2 border-t border-border/50" />);
        currentModule = null;
      }
      const mod = item.module || null;
      if (mod !== currentModule) {
        flushBatch();
        currentModule = mod;
      }
      currentBatch.push(renderItem(item, index));
    });
    flushBatch();

    return out;
  };

  const buildMenuItemRootWithBadge = (item, index) => {
    // Unique value using item.id (from DB) for reliable accordion state
    const itemValue = item.id ? `menu-${item.id}` : (item.path || `root-${index}`);

    if (item.children && item.children.length > 0) {
      // Hat der Trigger selbst einen Link? Dann klickbar machen!
      const hasOwnLink = item.path && item.path !== '#';

      return (
        <AccordionMenuSub key={item.id || index} value={itemValue}>
          <AccordionMenuSubTrigger
            className="text-sm font-medium"
            onClick={hasOwnLink ? (e) => handleItemClick(item.path || '', e) : undefined}
          >
            {hasOwnLink ? (
              <SmartLink
                href={item.path}
                isExternal={item.isExternal}
                target={item.target}
                router={router}
                className="flex items-center grow gap-2"
              >
                {item.icon && <item.icon data-slot="accordion-menu-icon" />}
                <span data-slot="accordion-menu-title">{item.title}</span>
                <Badge variant="outline" size="sm" className="ms-2 text-green-600 border-green-600">
                  {item.badge_text}
                </Badge>
              </SmartLink>
            ) : (
              <>
                {item.icon && <item.icon data-slot="accordion-menu-icon" />}
                <span data-slot="accordion-menu-title">{item.title}</span>
                <Badge variant="outline" size="sm" className="ms-2 text-green-600 border-green-600">
                  {item.badge_text}
                </Badge>
              </>
            )}
          </AccordionMenuSubTrigger>
          <AccordionMenuSubContent
            type="single"
            collapsible
            parentValue={itemValue}
            defaultValue={activeChildValue(item.children, 1)}
            className="ps-6"
          >
            <AccordionMenuGroup>
              {buildMenuItemChildren(item.children, 1)}
            </AccordionMenuGroup>
          </AccordionMenuSubContent>
        </AccordionMenuSub>
      );
    } else {
      return (
        <AccordionMenuItem
          key={item.id || index}
          value={itemValue}
          className="text-sm font-medium"
          onClick={(e) => handleItemClick(item.path || '', e)}
        >
          <SmartLink
            href={item.path || '#'}
            isExternal={item.isExternal}
            target={item.target}
            router={router}
            className="flex items-center grow gap-2"
          >
            {item.icon && <item.icon data-slot="accordion-menu-icon" />}
            <span data-slot="accordion-menu-title">{item.title}</span>
            <Badge variant="outline" size="sm" className="ms-2 text-green-600 border-green-600">
              {item.badge_text}
            </Badge>
          </SmartLink>
        </AccordionMenuItem>
      );
    }
  };

  const buildMenuItemRoot = (item, index) => {
    // Unique value using item.id (from DB) for reliable accordion state
    const itemValue = item.id ? `menu-${item.id}` : (item.path || `root-${index}`);

    if (item.children && item.children.length > 0) {
      // Hat der Trigger selbst einen Link? Dann klickbar machen!
      const hasOwnLink = item.path && item.path !== '#';

      return (
        <AccordionMenuSub key={item.id || index} value={itemValue}>
          <AccordionMenuSubTrigger
            className="text-sm font-medium"
            onClick={hasOwnLink ? (e) => handleItemClick(item.path || '', e) : undefined}
          >
            {hasOwnLink ? (
              <SmartLink
                href={item.path}
                isExternal={item.isExternal}
                target={item.target}
                router={router}
                className="flex items-center grow gap-2"
              >
                {item.icon && <item.icon data-slot="accordion-menu-icon" />}
                <span data-slot="accordion-menu-title">{item.title}</span>
              </SmartLink>
            ) : (
              <>
                {item.icon && <item.icon data-slot="accordion-menu-icon" />}
                <span data-slot="accordion-menu-title">{item.title}</span>
              </>
            )}
          </AccordionMenuSubTrigger>
          <AccordionMenuSubContent
            type="single"
            collapsible
            parentValue={itemValue}
            defaultValue={activeChildValue(item.children, 1)}
            className="ps-6"
          >
            <AccordionMenuGroup>
              {buildMenuItemChildren(item.children, 1)}
            </AccordionMenuGroup>
          </AccordionMenuSubContent>
        </AccordionMenuSub>
      );
    } else {
      return (
        <AccordionMenuItem
          key={index}
          value={item.path || ''}
          className={cn("text-sm font-medium", item.path && matchPath(item.path) && "bg-muted text-primary font-semibold")}
          onClick={(e) => handleItemClick(item.path || '', e)}
        >
          <SmartLink
            href={item.path || '#'}
            isExternal={item.isExternal}
            target={item.target}
            router={router}
            className="flex items-center grow gap-2"
          >
            {item.icon && <item.icon data-slot="accordion-menu-icon" />}
            <span data-slot="accordion-menu-title">{item.title}</span>
          </SmartLink>
        </AccordionMenuItem>
      );
    }
  };

  const buildMenuItemRootDisabled = (item, index) => {
    return (
      <AccordionMenuItem
        key={index}
        value={`disabled-${index}`}
        className="text-sm font-medium opacity-60"
      >
        {item.icon && <item.icon data-slot="accordion-menu-icon" />}
        <span data-slot="accordion-menu-title">{item.title}</span>
        <Badge variant="secondary" size="sm" className="ms-auto me-[-10px]">
          Soon
        </Badge>
      </AccordionMenuItem>
    );
  };

  const buildMenuItemChildren = (items, level = 0) => {
    return items.map((item, index) => {
      const divider = item.divider_before ? (
        <div key={`divider-child-${level}-${index}`} className="my-1.5 mx-2 border-t border-border/40" />
      ) : null;

      let menuItem;
      // Handle headings in nested items
      if (item.heading) {
        menuItem = buildMenuChildHeading(item, index, level);
      } else if (item.disabled) {
        menuItem = buildMenuItemChildDisabled(item, index, level);
      } else if (item.badge_text) {
        menuItem = buildMenuItemChildWithBadge(item, index, level);
      } else {
        menuItem = buildMenuItemChild(item, index, level);
      }

      return divider ? [divider, menuItem] : menuItem;
    }).flat();
  };

  // Child heading (section divider within nested menus)
  const buildMenuChildHeading = (item, index, level = 0) => {
    const depthBorder = getDepthBorderClass(level);
    return (
      <AccordionMenuLabel
        key={index}
        className={cn(
          "px-2 py-1.5 mt-2 first:mt-0 text-xs font-medium text-muted-foreground/70 uppercase",
          depthBorder
        )}
      >
        {item.heading}
      </AccordionMenuLabel>
    );
  };

  const buildMenuItemChildWithBadge = (item, index, level = 0) => {
    const depthBorder = getDepthBorderClass(level);
    // Unique value using item.id (from DB) for reliable accordion state
    const itemValue = item.id ? `menu-${item.id}` : (item.path || `child-${level}-${index}`);

    if (item.children && item.children.length > 0) {
      // Hat der Trigger selbst einen Link? Dann klickbar machen!
      const hasOwnLink = item.path && item.path !== '#';

      return (
        <AccordionMenuSub
          key={item.id || index}
          value={itemValue}
          className={depthBorder}
        >
          <AccordionMenuSubTrigger
            className={cn("text-2sm", hasOwnLink && matchPath(item.path) && "bg-muted text-primary font-medium")}
            onClick={hasOwnLink ? (e) => handleItemClick(item.path || '', e) : undefined}
          >
            {hasOwnLink ? (
              <SmartLink
                href={item.path}
                isExternal={item.isExternal}
                target={item.target}
                router={router}
                className="flex items-center grow gap-2"
              >
                {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
                <span data-slot="accordion-menu-title">{item.title}</span>
                <Badge variant="outline" size="sm" className="ms-2 text-green-600 border-green-600">
                  {item.badge_text}
                </Badge>
              </SmartLink>
            ) : (
              <>
                {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
                <span data-slot="accordion-menu-title">{item.title}</span>
                <Badge variant="outline" size="sm" className="ms-2 text-green-600 border-green-600">
                  {item.badge_text}
                </Badge>
              </>
            )}
          </AccordionMenuSubTrigger>
          <AccordionMenuSubContent
            type="single"
            collapsible
            parentValue={itemValue}
            defaultValue={activeChildValue(item.children, level + 1)}
            className="ps-4"
          >
            <AccordionMenuGroup>
              {buildMenuItemChildren(item.children, level + 1)}
            </AccordionMenuGroup>
          </AccordionMenuSubContent>
        </AccordionMenuSub>
      );
    } else {
      return (
        <AccordionMenuItem
          key={item.id || index}
          value={itemValue}
          className={cn("text-2sm", depthBorder)}
          onClick={(e) => handleItemClick(item.path || '', e)}
        >
          <SmartLink
            href={item.path || '#'}
            isExternal={item.isExternal}
            target={item.target}
            router={router}
            className="flex items-center grow gap-2"
          >
            {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
            <span data-slot="accordion-menu-title">{item.title}</span>
            <Badge variant="outline" size="sm" className="ms-2 text-green-600 border-green-600">
              {item.badge_text}
            </Badge>
          </SmartLink>
        </AccordionMenuItem>
      );
    }
  };

  const buildMenuItemChild = (item, index, level = 0) => {
    const depthBorder = getDepthBorderClass(level);
    // Unique value using item.id (from DB) for reliable accordion state
    const itemValue = item.id ? `menu-${item.id}` : (item.path || `child-${level}-${index}`);

    // Nur aufklappbar wenn WIRKLICH children mit Inhalt vorhanden sind
    if (item.children && item.children.length > 0) {
      // Hat der Trigger selbst einen Link? Dann klickbar machen!
      const hasOwnLink = item.path && item.path !== '#';

      return (
        <AccordionMenuSub
          key={item.id || index}
          value={itemValue}
          className={depthBorder}
        >
          <AccordionMenuSubTrigger
            className={cn("text-2sm", hasOwnLink && matchPath(item.path) && "bg-muted text-primary font-medium")}
            onClick={hasOwnLink ? (e) => handleItemClick(item.path || '', e) : undefined}
          >
            {hasOwnLink ? (
              <SmartLink
                href={item.path}
                isExternal={item.isExternal}
                target={item.target}
                router={router}
                className="flex items-center grow gap-2"
              >
                {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
                {item.collapse ? (
                  <span className="text-muted-foreground">
                    <span className="hidden [[data-state=open]>span>&]:inline">
                      {item.collapseTitle}
                    </span>
                    <span className="inline [[data-state=open]>span>&]:hidden">
                      {item.expandTitle}
                    </span>
                  </span>
                ) : (
                  <span data-slot="accordion-menu-title">{item.title}</span>
                )}
              </SmartLink>
            ) : (
              <>
                {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
                {item.collapse ? (
                  <span className="text-muted-foreground">
                    <span className="hidden [[data-state=open]>span>&]:inline">
                      {item.collapseTitle}
                    </span>
                    <span className="inline [[data-state=open]>span>&]:hidden">
                      {item.expandTitle}
                    </span>
                  </span>
                ) : (
                  <span data-slot="accordion-menu-title">{item.title}</span>
                )}
              </>
            )}
          </AccordionMenuSubTrigger>
          <AccordionMenuSubContent
            type="single"
            collapsible
            parentValue={itemValue}
            defaultValue={activeChildValue(item.children, item.collapse ? level : level + 1)}
            className={cn(
              'ps-4',
              !item.collapse && 'relative',
              !item.collapse && (level > 0 ? '' : ''),
            )}
          >
            <AccordionMenuGroup>
              {buildMenuItemChildren(
                item.children,
                item.collapse ? level : level + 1,
              )}
            </AccordionMenuGroup>
          </AccordionMenuSubContent>
        </AccordionMenuSub>
      );
    } else {
      return (
        <AccordionMenuItem
          key={item.id || index}
          value={itemValue}
          className={cn("text-2sm", depthBorder, item.path && matchPath(item.path) && "bg-muted text-primary font-medium")}
          onClick={(e) => handleItemClick(item.path || '', e)}
        >
          <SmartLink
            href={item.path || '#'}
            isExternal={item.isExternal}
            target={item.target}
            router={router}
            className="flex items-center grow gap-2"
          >
            {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
            <span data-slot="accordion-menu-title">{item.title}</span>
          </SmartLink>
        </AccordionMenuItem>
      );
    }
  };

  const buildMenuItemChildDisabled = (item, index, level = 0) => {
    const depthBorder = getDepthBorderClass(level);

    return (
      <AccordionMenuItem
        key={index}
        value={`disabled-child-${level}-${index}`}
        className={cn("text-2sm opacity-60", depthBorder)}
      >
        {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
        <span data-slot="accordion-menu-title">{item.title}</span>
        <Badge variant="secondary" size="sm" className="ms-auto me-[-10px]">
          Soon
        </Badge>
      </AccordionMenuItem>
    );
  };

  const buildMenuHeading = (item, index) => {
    return (
      <AccordionMenuLabel
        key={index}
        className="px-2 py-2 mt-4 first:mt-0 text-sm font-bold text-foreground uppercase tracking-wide"
      >
        {item.heading}
      </AccordionMenuLabel>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="kt-scrollable-y-hover flex grow shrink-0 py-3 px-4 lg:max-h-[calc(100vh-5.5rem)]">
        <div className="flex items-center justify-center w-full h-32">
          <div className="text-sm text-muted-foreground">Lade Menü...</div>
        </div>
      </div>
    );
  }

  // Build context menu items (reuses existing menu building logic)
  const buildContextMenu = (items) => {
    if (!items || items.length === 0) return null;
    return items.map((item, index) => {
      // Handle headings
      if (item.heading || item.type === 'header') {
        return (
          <AccordionMenuLabel
            key={`ctx-heading-${index}`}
            className="px-2 py-2 mt-4 first:mt-0 text-sm font-bold text-foreground uppercase tracking-wide"
          >
            {item.heading || item.title}
          </AccordionMenuLabel>
        );
      }

      const itemValue = item.id ? `ctx-menu-${item.id}` : `ctx-${index}`;

      // Items with children
      if (item.children && item.children.length > 0) {
        return (
          <AccordionMenuSub key={item.id || index} value={itemValue}>
            <AccordionMenuSubTrigger className="text-sm font-medium">
              {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
              <span data-slot="accordion-menu-title">{item.title}</span>
            </AccordionMenuSubTrigger>
            <AccordionMenuSubContent
              type="single"
              collapsible
              parentValue={itemValue}
              className="ps-6"
            >
              <AccordionMenuGroup>
                {buildContextMenuChildren(item.children, 1)}
              </AccordionMenuGroup>
            </AccordionMenuSubContent>
          </AccordionMenuSub>
        );
      }

      // Leaf items
      return (
        <AccordionMenuItem
          key={item.id || index}
          value={itemValue}
          className="text-sm"
          onClick={(e) => handleItemClick(item.path || '', e)}
        >
          <SmartLink
            href={item.path || '#'}
            isExternal={false}
            target={item.target}
            router={router}
            className="flex items-center grow gap-2"
          >
            {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
            <span data-slot="accordion-menu-title">{item.title}</span>
          </SmartLink>
        </AccordionMenuItem>
      );
    });
  };

  // Build context menu children (nested items)
  const buildContextMenuChildren = (items, level = 1) => {
    if (!items || items.length === 0) return null;
    const depthBorder = getDepthBorderClass(level);

    return items.map((item, index) => {
      const itemValue = item.id ? `ctx-menu-${item.id}` : `ctx-child-${level}-${index}`;

      if (item.children && item.children.length > 0) {
        return (
          <AccordionMenuSub key={item.id || index} value={itemValue} className={depthBorder}>
            <AccordionMenuSubTrigger className="text-2sm">
              {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
              <span data-slot="accordion-menu-title">{item.title}</span>
            </AccordionMenuSubTrigger>
            <AccordionMenuSubContent
              type="single"
              collapsible
              parentValue={itemValue}
              className="ps-4"
            >
              <AccordionMenuGroup>
                {buildContextMenuChildren(item.children, level + 1)}
              </AccordionMenuGroup>
            </AccordionMenuSubContent>
          </AccordionMenuSub>
        );
      }

      return (
        <AccordionMenuItem
          key={item.id || index}
          value={itemValue}
          className={cn("text-2sm", depthBorder)}
          onClick={(e) => handleItemClick(item.path || '', e)}
        >
          <SmartLink
            href={item.path || '#'}
            isExternal={false}
            target={item.target}
            router={router}
            className="flex items-center grow gap-2"
          >
            {item.icon && <item.icon data-slot="accordion-menu-icon" size={16} />}
            <span data-slot="accordion-menu-title">{item.title}</span>
          </SmartLink>
        </AccordionMenuItem>
      );
    });
  };

  return (
    <div className="kt-scrollable-y-hover flex flex-col grow shrink-0 py-3 px-4 lg:max-h-[calc(100vh-5.5rem)]">
      {/* Main Menu */}
      <AccordionMenu
        type="single"
        collapsible
        value={openValue}
        onValueChange={setOpenValue}
        className={classNames.root}
      >
        {buildMenu(currentMenu)}
      </AccordionMenu>

      {/* (Legacy "Context Menu for KAIE entities" block removed — the main menu
          already shows entity-specific items via the manifest's show_when filter,
          so the secondary block was duplicating the entity name. The
          `#{entity_name}#` separator from each container's manifest now serves
          as the section heading.) */}
    </div>
  );
}
